// @ts-ignore
import mixpanel from 'mixpanel-browser';

type MixpanelInternal = typeof mixpanel & {
  _flags?: {
    disable_all_events?: boolean;
    identify_called?: boolean;
  };
  get_config?: (name: string) => unknown;
  get_distinct_id?: () => string;
};

const getMixpanelInstance = () => mixpanel as MixpanelInternal;

export const isMixpanelReady = () => {
  const mixpanelInstance = getMixpanelInstance();
  const hasFlags = typeof mixpanelInstance._flags?.disable_all_events === 'boolean';
  const token = mixpanelInstance.get_config?.('token');

  return hasFlags && typeof token === 'string' && token.length > 0;
};

const runMixpanelSafely = <T>(callback: (instance: typeof mixpanel) => T): T | undefined => {
  if (!isMixpanelReady()) return undefined;

  try {
    return callback(mixpanel);
  } catch (error) {
    console.warn('Mixpanel call skipped after runtime error:', error);
    return undefined;
  }
};

/**
 * Safely track an event with mixpanel, only if mixpanel is initialized
 */
export const safeTrackMixpanel = (eventName: string, properties?: Record<string, unknown>) => {
  runMixpanelSafely((instance) => instance.track(eventName, properties));
};

const mixpanelLogger = {
  init: (...args: Parameters<typeof mixpanel.init>) => mixpanel.init(...args),
  track: (...args: Parameters<typeof mixpanel.track>) =>
    runMixpanelSafely((instance) => instance.track(...args)),
  identify: (...args: Parameters<typeof mixpanel.identify>) =>
    runMixpanelSafely((instance) => instance.identify(...args)),
  get_distinct_id: () =>
    runMixpanelSafely((instance) => getMixpanelInstance().get_distinct_id?.call(instance)),
};

export default mixpanelLogger;
